function fact(t, key, value, code = false) {
  return {
    label: t(`visualGameAdmin.llm.runVideoMatch.execution.facts.${key}`),
    value,
    code,
  }
}

function statusSummary(t, group, status, params = {}) {
  return t(`visualGameAdmin.llm.runVideoMatch.execution.summary.${group}.${status}`, params)
}

export function stepExecutionDetail({ step, stepIndex, turn, t }) {
  const matchInput = turn.match_input
  const matchPolicy = turn.match_policy
  switch (step.kind) {
    case 'historical_capture':
      return { summary: step.reason, facts: [] }
    case 'cg_recall_gate':
      return {
        summary: statusSummary(t, 'cgRecallGate', step.status),
        facts: [fact(t, 'switchResult', t(`visualGameAdmin.llm.videoMatch.status.${step.status}`))],
      }
    case 'smart_match_setting':
      return {
        summary: statusSummary(t, 'smartMatchSetting', step.status),
        facts: [fact(t, 'settingResult', t(`visualGameAdmin.llm.videoMatch.status.${step.status}`))],
      }
    case 'exact_match':
      return {
        summary: statusSummary(t, 'exactMatch', step.status),
        facts: [
          fact(t, 'queryTarget', t('visualGameAdmin.llm.runVideoMatch.execution.exactResourceStore')),
          fact(t, 'scenarioVersion', matchInput.version_id, true),
          fact(t, 'transitionHash', matchInput.transition_hash, true),
          fact(t, 'resourceScope', resourceScope(t, matchPolicy.storyboard_recall)),
          fact(t, 'queryResult', exactMatchResult(t, step.status)),
          fact(t, 'nextStep', actualNextStep(t, turn, stepIndex)),
          fact(t, 'externalCall', t('visualGameAdmin.llm.runVideoMatch.execution.noExternalCall')),
        ],
      }
    case 'saved_semantic_match':
      return {
        summary: statusSummary(t, 'savedSemanticMatch', step.status),
        facts: [
          fact(t, 'queryTarget', t('visualGameAdmin.llm.runVideoMatch.execution.semanticBindingStore')),
          fact(t, 'scenarioVersion', matchInput.version_id, true),
          fact(t, 'transitionHash', matchInput.transition_hash, true),
          fact(t, 'bindingStatus', t('visualGameAdmin.llm.runVideoMatch.execution.activeBinding')),
          fact(t, 'queryResult', savedSemanticMatchResult(t, step.status)),
          fact(t, 'nextStep', actualNextStep(t, turn, stepIndex)),
          fact(t, 'externalCall', t('visualGameAdmin.llm.runVideoMatch.execution.noExternalCall')),
        ],
      }
    case 'semantic_candidate_bucket':
      return {
        summary: statusSummary(t, 'semanticCandidateBucket', step.status),
        facts: [
          fact(t, 'queryTarget', t('visualGameAdmin.llm.runVideoMatch.execution.sceneVectorIndex')),
          fact(t, 'resourceScope', resourceScope(t, matchPolicy.storyboard_recall)),
          fact(t, 'queryResult', semanticBucketResult(t, step.status)),
          fact(t, 'nextStep', actualNextStep(t, turn, stepIndex)),
          fact(t, 'externalCall', t('visualGameAdmin.llm.runVideoMatch.execution.noExternalCall')),
        ],
      }
    case 'semantic_embedding':
      return {
        summary: statusSummary(t, 'semanticEmbedding', step.status),
        facts: [fact(t, 'inputSource', t('visualGameAdmin.llm.runVideoMatch.execution.semanticInputSource'))],
      }
    case 'semantic_vector_search':
      return {
        summary: statusSummary(t, 'semanticVectorSearch', step.status, { count: step.candidates.length }),
        facts: [
          fact(t, 'queryTarget', t('visualGameAdmin.llm.runVideoMatch.execution.sceneVectorIndex')),
          fact(t, 'queryResult', queryCount(t, step.candidates.length)),
          fact(t, 'rankingRule', t('visualGameAdmin.llm.runVideoMatch.execution.similarityDescending')),
          fact(t, 'nextDecision', t('visualGameAdmin.llm.runVideoMatch.execution.semanticDecisionNext')),
          fact(t, 'externalCall', t('visualGameAdmin.llm.runVideoMatch.execution.localVectorSearch')),
        ],
      }
    case 'semantic_rerank':
      return {
        summary: statusSummary(t, 'semanticDecision', step.status),
        facts: [fact(t, 'decisionMethod', t('visualGameAdmin.llm.runVideoMatch.execution.llmDecision'))],
      }
    case 'intent_llm_selection':
      return {
        summary: statusSummary(t, 'intentSelection', step.status),
        facts: [fact(t, 'decisionMethod', t('visualGameAdmin.llm.runVideoMatch.execution.intentLlmDecision'))],
      }
    case 'intent_embedding':
    case 'intent_demand_embedding':
      return {
        summary: statusSummary(t, 'intentEmbedding', step.status),
        facts: [fact(t, 'inputSource', t('visualGameAdmin.llm.runVideoMatch.execution.playerInputSource'))],
      }
    case 'intent_vector_search':
      return intentVectorSearchDetail({ step, turn, t })
    case 'result':
      return {
        summary: statusSummary(t, 'result', step.status),
        facts: resultFacts(step, t),
      }
    default:
      return {
        summary: step.reason ?? `${step.kind} · ${step.status}`,
        facts: [],
      }
  }
}

function resultFacts(step, t) {
  switch (step.status) {
    case 'uncovered':
      return []
    case 'selected':
      switch (step.method) {
        case 'exact_transition':
          return [fact(t, 'selectedResource', step.resource_id, true)]
        case 'semantic_reuse':
        case 'intent_video':
          return [
            fact(t, 'selectedResource', step.resource_id, true),
            fact(t, 'finalSimilarity', step.similarity.toFixed(4)),
          ]
        default:
          return step.resource_id === null
            ? []
            : [fact(t, 'selectedResource', step.resource_id, true)]
      }
    default:
      return []
  }
}

function intentVectorSearchDetail({ step, turn, t }) {
  const eligibleCount = step.candidates.filter(candidate => (
    candidate.similarity >= step.minimum_similarity_threshold
  )).length
  const facts = [
    fact(t, 'queryTarget', t('visualGameAdmin.llm.runVideoMatch.execution.intentVectorIndex')),
    fact(t, 'scenarioVersion', turn.match_input.version_id, true),
    fact(t, 'candidateLimit', String(turn.match_policy.intent_recall_limit)),
    fact(t, 'resourceScope', intentResourceScope(t, turn.match_policy.storyboard_recall)),
    fact(t, 'queryResult', queryCount(t, step.candidates.length)),
    fact(t, 'minimumSimilarityThreshold', step.minimum_similarity_threshold.toFixed(4)),
    fact(t, 'directMatchThreshold', step.direct_match_threshold.toFixed(4)),
    fact(t, 'eligibleCandidateCount', queryCount(t, eligibleCount)),
    fact(t, 'rankingRule', t('visualGameAdmin.llm.runVideoMatch.execution.similarityDescending')),
    fact(t, 'decisionMethod', intentDecisionMethod(t, step.status)),
    fact(t, 'externalCall', t('visualGameAdmin.llm.runVideoMatch.execution.localVectorSearch')),
  ]
  switch (step.status) {
    case 'selected': {
      const best = step.candidates[0]
      return {
        summary: statusSummary(t, 'intentVectorSearch', step.status, {
          limit: turn.match_policy.intent_recall_limit,
          similarity: best.similarity.toFixed(4),
          threshold: step.direct_match_threshold.toFixed(4),
          resource: step.selected_resource_id,
        }),
        facts,
      }
    }
    case 'needs_llm_selection': {
      const best = step.candidates[0]
      return {
        summary: statusSummary(t, 'intentVectorSearch', step.status, {
          limit: turn.match_policy.intent_recall_limit,
          similarity: best.similarity.toFixed(4),
          minimum: step.minimum_similarity_threshold.toFixed(4),
          direct: step.direct_match_threshold.toFixed(4),
          eligible: eligibleCount,
        }),
        facts,
      }
    }
    case 'below_minimum': {
      const best = step.candidates[0]
      return {
        summary: statusSummary(t, 'intentVectorSearch', step.status, {
          limit: turn.match_policy.intent_recall_limit,
          similarity: best.similarity.toFixed(4),
          minimum: step.minimum_similarity_threshold.toFixed(4),
        }),
        facts,
      }
    }
    case 'empty':
      return {
        summary: statusSummary(t, 'intentVectorSearch', step.status, {
          limit: turn.match_policy.intent_recall_limit,
        }),
        facts,
      }
    default:
      return { summary: `${step.kind} · ${step.status}`, facts }
  }
}

function intentDecisionMethod(t, status) {
  switch (status) {
    case 'selected':
      return t('visualGameAdmin.llm.runVideoMatch.execution.topOneThresholdNoLlm')
    case 'needs_llm_selection':
      return t('visualGameAdmin.llm.runVideoMatch.execution.intentSelectionNext')
    case 'below_minimum':
      return t('visualGameAdmin.llm.runVideoMatch.execution.belowMinimumNoLlm')
    case 'empty':
      return t('visualGameAdmin.llm.runVideoMatch.execution.noCandidateDecision')
    default:
      return status
  }
}

function queryCount(t, count) {
  return t('visualGameAdmin.llm.runVideoMatch.execution.queryCount', { count })
}

function exactMatchResult(t, status) {
  switch (status) {
    case 'selected':
      return queryCount(t, 1)
    case 'miss':
      return queryCount(t, 0)
    default:
      return status
  }
}

function savedSemanticMatchResult(t, status) {
  switch (status) {
    case 'selected':
      return queryCount(t, 1)
    case 'miss':
      return queryCount(t, 0)
    default:
      return status
  }
}

function semanticBucketResult(t, status) {
  switch (status) {
    case 'available':
      return t('visualGameAdmin.llm.runVideoMatch.execution.bucketAvailable')
    case 'empty':
      return queryCount(t, 0)
    default:
      return status
  }
}

function actualNextStep(t, turn, stepIndex) {
  const nextStep = turn.trace.steps[stepIndex + 1]
  if (nextStep === undefined) return '—'
  return t(`visualGameAdmin.llm.videoMatch.steps.${nextStep.kind}`)
}

function resourceScope(t, storyboardRecall) {
  return storyboardRecall
    ? t('visualGameAdmin.llm.runVideoMatch.execution.playableStoryboards')
    : t('visualGameAdmin.llm.runVideoMatch.execution.playableVideos')
}

function intentResourceScope(t, storyboardRecall) {
  return storyboardRecall
    ? t('visualGameAdmin.llm.runVideoMatch.execution.intentStoryboards')
    : t('visualGameAdmin.llm.runVideoMatch.execution.intentVideos')
}
